import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  FiCheck,
  FiDownload,
  FiEye,
  FiFileText,
  FiUpload,
  FiX,
} from "react-icons/fi";

import vehicleDocumentsService from "../../services/vehicle_document_service";

import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { useConfirm } from "../../components/ConfirmDialog/ConfirmDialog";

import "./VehicleDocumentsForm.css";


const DOCUMENT_TYPES = [
  {
    key: "rc",
    label: "Registration Certificate",
    required: true,
  },
  {
    key: "permit",
    label: "Permit",
    required: true,
  },
  {
    key: "fitness",
    label: "Fitness Certificate",
    required: true,
  },
  {
    key: "insurance",
    label: "Insurance",
    required: true,
  },
  {
    key: "police_sticker",
    label: "Police Sticker",
    required: false,
  },
  {
    key: "tax",
    label: "Road Tax",
    required: false,
  },
  {
    key: "pollution",
    label: "Pollution (PUC)",
    required: false,
  },
  {
    key: "city_police_permit",
    label: "City Police Permit",
    required: false,
  },
];


const REQUIRED_DOCUMENT_TYPES =
  DOCUMENT_TYPES.filter(
    (document) => document.required
  );


const VEHICLE_DOCUMENTS_LIST_PATH =
  "/vehicle-documents";


const createEmptyDocState = () =>
  DOCUMENT_TYPES.reduce(
    (result, document) => {
      result[document.key] = {
        expiry_date: "",
        file_url: "",
        original_filename: "",
        uploading: false,
      };

      return result;
    },
    {}
  );


function formatUTCTimestamp(value) {
  if (!value) {
    return "—";
  }

  const hasOffset =
    /Z$|[+-]\d{2}(:?\d{2})?$/.test(
      value
    );

  const isoUTC = hasOffset
    ? value
    : `${value}Z`;

  const date = new Date(isoUTC);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString(
    "en-IN",
    {
      timeZone: "Asia/Kolkata",
      dateStyle: "medium",
      timeStyle: "short",
    }
  );
}


function resolveUserName(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  if (
    typeof value === "object"
  ) {
    return (
      value.name ||
      value.full_name ||
      value.username ||
      value.email ||
      null
    );
  }

  return String(value);
}


const IMAGE_EXT_RE =
  /\.(png|jpe?g|gif|webp|bmp|svg|avif)(\?.*)?$/i;


const PDF_EXT_RE =
  /\.pdf(\?.*)?$/i;


const isImageFile = (
  url,
  filename
) =>
  IMAGE_EXT_RE.test(
    url || ""
  ) ||
  IMAGE_EXT_RE.test(
    filename || ""
  );


const isPdfFile = (
  url,
  filename
) =>
  PDF_EXT_RE.test(
    url || ""
  ) ||
  PDF_EXT_RE.test(
    filename || ""
  );


function SearchableSelect({
  options = [],
  value,
  onChange,
  onSearch,
  loading = false,
  error = null,
  placeholder = "Select…",
  disabled = false,
}) {
  const [
    open,
    setOpen,
  ] = useState(false);

  const [
    query,
    setQuery,
  ] = useState("");

  const containerRef =
    useRef(null);

  const debounceRef =
    useRef(null);

  const selectedOption =
    options.find(
      (option) =>
        String(option.value) ===
        String(value)
    );


  useEffect(() => {
    const handleClick = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(
          event.target
        )
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClick
      );

      if (debounceRef.current) {
        clearTimeout(
          debounceRef.current
        );
      }
    };
  }, []);


  const handleInputChange = (
    event
  ) => {
    const value =
      event.target.value;

    setQuery(value);
    setOpen(true);

    if (onSearch) {
      if (debounceRef.current) {
        clearTimeout(
          debounceRef.current
        );
      }

      debounceRef.current =
        setTimeout(() => {
          onSearch(value);
        }, 300);
    }
  };


  const handleSelect = (
    option
  ) => {
    onChange(
      option.value,
      option
    );

    setQuery("");
    setOpen(false);
  };


  const handleFocus = () => {
    if (disabled) {
      return;
    }

    setOpen(true);

    if (onSearch) {
      onSearch("");
    }
  };


  return (
    <div
      ref={containerRef}
      className={`ss-container ${
        disabled
          ? "ss-disabled"
          : ""
      }`}
    >
      <input
        className="ss-input"
        type="text"
        disabled={disabled}
        placeholder={
          selectedOption
            ? selectedOption.label
            : placeholder
        }
        value={
          open
            ? query
            : ""
        }
        onFocus={handleFocus}
        onChange={
          handleInputChange
        }
      />

      {open && !disabled && (
        <div className="ss-panel">
          {loading && (
            <div className="ss-status">
              Searching…
            </div>
          )}

          {!loading &&
            error && (
              <div className="ss-status ss-status-error">
                {error}
              </div>
            )}

          {!loading &&
            !error &&
            options.length === 0 && (
              <div className="ss-status">
                No matches
              </div>
            )}

          {!loading &&
            !error &&
            options.map(
              (option) => (
                <div
                  key={option.value}
                  className={`ss-option ${
                    String(
                      option.value
                    ) ===
                    String(value)
                      ? "ss-option--active"
                      : ""
                  }`}
                  onMouseDown={() =>
                    handleSelect(
                      option
                    )
                  }
                >
                  <span className="ss-option-label">
                    {option.label}
                  </span>

                  {option.sublabel && (
                    <span className="ss-option-sublabel">
                      {
                        option.sublabel
                      }
                    </span>
                  )}
                </div>
              )
            )}
        </div>
      )}
    </div>
  );
}


export default function VehicleDocument() {
  const navigate =
    useNavigate();

  const {
    vehicleId: routeVehicleId,
  } = useParams();

  const confirm =
    useConfirm();


  const [
    vehicleOptions,
    setVehicleOptions,
  ] = useState([]);


  const [
    vehicleSearchLoading,
    setVehicleSearchLoading,
  ] = useState(false);


  const [
    vehicleSearchError,
    setVehicleSearchError,
  ] = useState(null);


  const [
    vehicleId,
    setVehicleId,
  ] = useState("");


  const [
    autofill,
    setAutofill,
  ] = useState(null);


  const [
    displayCode,
    setDisplayCode,
  ] = useState("");


  const [
    branchId,
    setBranchId,
  ] = useState("");


  const [
    branches,
    setBranches,
  ] = useState([]);


  const [
    docState,
    setDocState,
  ] = useState(
    createEmptyDocState()
  );


  const [
    documentsRowId,
    setDocumentsRowId,
  ] = useState(null);


  const [
    documentsMeta,
    setDocumentsMeta,
  ] = useState({
    createdAt: null,
    updatedAt: null,
    createdBy: null,
    updatedBy: null,
  });


  const [
    loadingVehicleData,
    setLoadingVehicleData,
  ] = useState(false);


  const [
    saving,
    setSaving,
  ] = useState(false);


  const [
    status,
    setStatus,
  ] = useState(null);


  const [
    downloadingKey,
    setDownloadingKey,
  ] = useState(null);


  const [
    previewDoc,
    setPreviewDoc,
  ] = useState(null);


    const [
    previewLoading,
    setPreviewLoading,
  ] = useState(false);
const isEditMode =
    Boolean(routeVehicleId);


  const resetDocumentFields =
    useCallback(() => {
      setDocState(
        createEmptyDocState()
      );

      setDocumentsRowId(
        null
      );

      setDocumentsMeta({
        createdAt: null,
        updatedAt: null,
        createdBy: null,
        updatedBy: null,
      });
    }, []);


  const searchVehicles =
    useCallback(
      async (query) => {
        setVehicleSearchLoading(
          true
        );

        setVehicleSearchError(
          null
        );

        try {
          const rows =
            await vehicleDocumentsService.getVehicles(
              {
                search:
                  query ||
                  undefined,
              }
            );

          setVehicleOptions(
            rows.map((vehicle) => ({
              value: vehicle.id,
              label:
                vehicle.vehicle_registration_number,
              sublabel:
                vehicle.vehicle_display_number ||
                undefined,
            }))
          );
        } catch (error) {
          const statusCode =
            error.response?.status;

          setVehicleOptions([]);

          if (
            statusCode === 401
          ) {
            setVehicleSearchError(
              "Session expired. Please log in again."
            );
          } else if (
            statusCode
          ) {
            setVehicleSearchError(
              `Search failed (${statusCode}).`
            );
          } else {
            setVehicleSearchError(
              "Network error."
            );
          }
        } finally {
          setVehicleSearchLoading(
            false
          );
        }
      },
      []
    );


  const handleVehicleSelect =
    useCallback(
      async (id) => {
        setVehicleId(id);
        setStatus(null);
        setLoadingVehicleData(
          true
        );

        resetDocumentFields();

        try {
          const auto =
            await vehicleDocumentsService.getVehicleAutofill(
              id
            );

          setAutofill(auto);

          setDisplayCode(
            auto.display_code ||
              ""
          );

          setBranchId("");

          if (
            auto.service_location_id
          ) {
            try {
              const branchRows =
                await vehicleDocumentsService.getBranches(
                  auto.service_location_id
                );

              setBranches(
                branchRows
              );
            } catch {
              setBranches([]);
            }
          } else {
            setBranches([]);
          }

          try {
            const existing =
              await vehicleDocumentsService.getVehicleDocuments(
                id
              );

            setDocumentsRowId(
              existing.id
            );

            setBranchId(
              existing.branch_id ||
                ""
            );

            setDocumentsMeta({
              createdAt:
                existing.created_at ||
                null,
              updatedAt:
                existing.updated_at ||
                null,
              createdBy:
                resolveUserName(
                  existing.created_by_name ||
                    existing.created_by
                ),
              updatedBy:
                resolveUserName(
                  existing.updated_by_name ||
                    existing.updated_by
                ),
            });

            setDocState(
              () => {
                const next =
                  createEmptyDocState();

                DOCUMENT_TYPES.forEach(
                  ({ key }) => {
                    next[key] = {
                      expiry_date:
                        existing[
                          `${key}_expiry_date`
                        ] || "",
                      file_url:
                        existing[
                          `${key}_file_url`
                        ] || "",
                      original_filename:
                        "",
                      uploading:
                        false,
                    };
                  }
                );

                return next;
              }
            );
          } catch (
            error
          ) {
            if (
              error.response
                ?.status !== 404
            ) {
              throw error;
            }
          }
        } catch (error) {
          console.error(
            "Could not load vehicle details:",
            error
          );

          setStatus({
            type: "error",
            message:
              "Could not load vehicle details. Please try again.",
          });
        } finally {
          setLoadingVehicleData(
            false
          );
        }
      },
      [resetDocumentFields]
    );


  useEffect(() => {
    if (routeVehicleId) {
      handleVehicleSelect(
        routeVehicleId
      );
    }
  }, [
    routeVehicleId,
    handleVehicleSelect,
  ]);


  const handleDateChange = (
    key,
    value
  ) => {
    setDocState(
      (previous) => ({
        ...previous,
        [key]: {
          ...previous[key],
          expiry_date:
            value,
        },
      })
    );
  };


  const handleFileChange =
    async (
      key,
      file
    ) => {
      if (
        !file ||
        !vehicleId
      ) {
        return;
      }

      setDocState(
        (previous) => ({
          ...previous,
          [key]: {
            ...previous[key],
            uploading:
              true,
          },
        })
      );

      try {
        const result =
          await vehicleDocumentsService.uploadDocumentFile(
            vehicleId,
            file
          );

        setDocState(
          (previous) => ({
            ...previous,
            [key]: {
              ...previous[key],
              file_url:
                result.file_url,
              original_filename:
                result.original_filename,
              uploading:
                false,
            },
          })
        );
      } catch (error) {
        setDocState(
          (previous) => ({
            ...previous,
            [key]: {
              ...previous[key],
              uploading:
                false,
            },
          })
        );

        setStatus({
          type: "error",
          message: `Could not upload ${key.replace(
            "_",
            " "
          )} file.`,
        });
      }
    };


  const openPreview = async (
    url,
    label,
    filename
  ) => {
    if (!url) {
      return;
    }

    setPreviewLoading(true);

    try {
      const result =
        await vehicleDocumentsService.downloadFile(
          url,
          filename || label || "document"
        );

      const blob = result?.blob;

      if (!blob) {
        throw new Error("Preview file is empty");
      }

      const blobUrl = URL.createObjectURL(blob);
      const resolvedUrl =
        vehicleDocumentsService.resolveFileUrl(
          url
        ) || "";

      const contentType =
        blob.type || "";

      const kind =
        contentType.includes("pdf") ||
        isPdfFile(resolvedUrl, filename)
          ? "pdf"
          : contentType.startsWith("image/") ||
            isImageFile(resolvedUrl, filename)
          ? "image"
          : "other";

      setPreviewDoc({
        url: blobUrl,
        label,
        kind,
        originalUrl: resolvedUrl,
      });
    } catch (error) {
      console.error("Preview file failed:", error);
      setStatus({
        type: "error",
        message: "Could not preview the file. Please try again.",
      });
    } finally {
      setPreviewLoading(false);
    }
  };


  const closePreview =
    () => {
      if (previewDoc?.url?.startsWith("blob:")) {
        URL.revokeObjectURL(previewDoc.url);
      }
      setPreviewDoc(null);
    };


  const handleDownload =
    async (
      url,
      filename,
      key
    ) => {
      if (!url) {
        return;
      }

      setDownloadingKey(
        key || url
      );

      try {
        const {
          blob,
        } =
          await vehicleDocumentsService.downloadFile(
            url,
            filename
          );

        const blobUrl =
          URL.createObjectURL(
            blob
          );

        const anchor =
          document.createElement(
            "a"
          );

        anchor.href =
          blobUrl;

        anchor.download =
          filename ||
          "document";

        document.body.appendChild(
          anchor
        );

        anchor.click();

        anchor.remove();

        URL.revokeObjectURL(
          blobUrl
        );
      } catch (error) {
        setStatus({
          type: "error",
          message:
            "Could not download the file.",
        });
      } finally {
        setDownloadingKey(
          null
        );
      }
    };


  const handleBack =
    async () => {
      const confirmed =
        await confirm({
          title:
            "Unsaved Changes",
          message:
            "Are you sure you want to go back? Any unsaved changes will be lost.",
          confirmLabel:
            "Go Back",
          cancelLabel:
            "Stay on Page",
          danger: true,
        });

      if (confirmed) {
        navigate(
          VEHICLE_DOCUMENTS_LIST_PATH
        );
      }
    };


  const validateRequiredDocuments =
    () => {
      const missing =
        REQUIRED_DOCUMENT_TYPES.filter(
          ({ key }) => {
            const document =
              docState[key];

            return (
              !document.expiry_date ||
              !document.file_url
            );
          }
        );

      return missing.map(
        (document) =>
          document.label
      );
    };


  const handleSave =
    async () => {
      if (!vehicleId) {
        setStatus({
          type: "error",
          message:
            "Select a vehicle before saving.",
        });

        return;
      }

      const missingLabels =
        validateRequiredDocuments();

      if (
        missingLabels.length >
        0
      ) {
        setStatus({
          type: "error",
          message:
            `Please add expiry date and upload file for: ${missingLabels.join(
              ", "
            )}.`,
        });

        return;
      }

      const uploadingRequired =
        REQUIRED_DOCUMENT_TYPES.some(
          ({ key }) =>
            docState[key]
              .uploading
        );

      if (
        uploadingRequired
      ) {
        setStatus({
          type: "error",
          message:
            "Please wait until required document uploads finish.",
        });

        return;
      }

      setSaving(true);
      setStatus(null);

      const payload = {
        vehicle_id:
          Number(vehicleId),

        service_location_id:
          autofill?.service_location_id ??
          null,

        branch_id:
          branchId
            ? Number(branchId)
            : null,

        company_id:
          autofill?.company_id ??
          null,

        vehicle_make_id:
          autofill?.vehicle_make_id ??
          null,

        vehicle_model_id:
          autofill?.vehicle_model_id ??
          null,

        display_code:
          displayCode || null,
      };

      DOCUMENT_TYPES.forEach(
        ({ key }) => {
          payload[
            `${key}_expiry_date`
          ] =
            docState[key]
              .expiry_date ||
            null;

          payload[
            `${key}_file_url`
          ] =
            docState[key]
              .file_url ||
            null;
        }
      );

      try {
        const saved =
          await vehicleDocumentsService.saveVehicleDocuments(
            payload
          );

        setDocumentsRowId(
          saved?.id ||
            documentsRowId
        );

        setDocumentsMeta({
          createdAt:
            saved?.created_at ||
            documentsMeta.createdAt,

          updatedAt:
            saved?.updated_at ||
            null,

          createdBy:
            resolveUserName(
              saved?.created_by_name ||
                saved?.created_by
            ) ||
            documentsMeta.createdBy,

          updatedBy:
            resolveUserName(
              saved?.updated_by_name ||
                saved?.updated_by
            ),
        });

        setStatus({
          type: "success",
          message:
            "Vehicle documents saved successfully.",
        });

        setTimeout(() => {
          navigate(
            VEHICLE_DOCUMENTS_LIST_PATH
          );
        }, 900);
      } catch (error) {
        console.error(
          "Save vehicle documents failed:",
          error.response?.data ||
            error
        );

        const detail =
          error.response?.data
            ?.detail;

        let message =
          "Save failed. Please check the form and try again.";

        if (
          Array.isArray(
            detail
          )
        ) {
          message =
            detail
              .map(
                (item) =>
                  item.msg
              )
              .join(", ");
        } else if (
          typeof detail ===
          "string"
        ) {
          message =
            detail;
        }

        setStatus({
          type: "error",
          message,
        });
      } finally {
        setSaving(false);
      }
    };


  return (
    <div className="vdl-form-shell">
      <Sidebar />

      <div className="vdl-form-main">
        <Header />

        <div className="vdl-form-page">
          <div className="vdl-form-card">

            <div className="vdl-form-section">
              <h2>
                Find Vehicle
              </h2>

              <div className="vdl-form-grid">

                <div className="vdl-form-field">
                  <span>
                    Vehicle Number
                  </span>

                  <SearchableSelect
                    options={
                      vehicleOptions
                    }
                    value={
                      vehicleId
                    }
                    onChange={
                      handleVehicleSelect
                    }
                    onSearch={
                      searchVehicles
                    }
                    loading={
                      vehicleSearchLoading
                    }
                    error={
                      vehicleSearchError
                    }
                    placeholder="Search reg. no. or display code"
                    disabled={
                      isEditMode
                    }
                  />
                </div>

                <label className="vdl-form-field">
                  <span>
                    Branch
                  </span>

                  <select
                    value={
                      branchId
                    }
                    disabled={
                      !autofill
                    }
                    onChange={(
                      event
                    ) =>
                      setBranchId(
                        event
                          .target
                          .value
                      )
                    }
                  >
                    <option value="">
                      Select branch
                    </option>

                    {branches.map(
                      (branch) => (
                        <option
                          key={
                            branch.id
                          }
                          value={
                            branch.id
                          }
                        >
                          {
                            branch.branch_name
                          }
                        </option>
                      )
                    )}
                  </select>
                </label>

              </div>
            </div>


            <div className="vdl-form-section">
              <h2>
                Vehicle Details
              </h2>

              <div className="vdl-form-grid">

                <label className="vdl-form-field">
                  <span>
                    Registration Number
                  </span>

                  <input
                    readOnly
                    value={
                      autofill?.vehicle_registration_number ||
                      "—"
                    }
                  />
                </label>

                <label className="vdl-form-field">
                  <span>
                    Make
                  </span>

                  <input
                    readOnly
                    value={
                      autofill?.vehicle_make_name ||
                      "—"
                    }
                  />
                </label>

                <label className="vdl-form-field">
                  <span>
                    Model
                  </span>

                  <input
                    readOnly
                    value={
                      autofill?.vehicle_model_name ||
                      "—"
                    }
                  />
                </label>

                <label className="vdl-form-field">
                  <span>
                    Display Code
                  </span>

                  <input
                    value={
                      displayCode
                    }
                    maxLength={4}
                    disabled={
                      !autofill
                    }
                    onChange={(
                      event
                    ) =>
                      setDisplayCode(
                        event.target.value.replace(
                          /\D/g,
                          ""
                        )
                      )
                    }
                    placeholder="0000"
                  />
                </label>

                <label className="vdl-form-field">
                  <span>
                    Service Location
                  </span>

                  <input
                    readOnly
                    value={
                      autofill?.service_location_name ||
                      "—"
                    }
                  />
                </label>

                <label className="vdl-form-field">
                  <span>
                    Company
                  </span>

                  <input
                    readOnly
                    value={
                      autofill?.company_name ||
                      "—"
                    }
                  />
                </label>

              </div>
            </div>


            <div className="vdl-form-section">
              <h2>
                Documents
              </h2>

              <div className="vdl-form-doc-grid">

                {DOCUMENT_TYPES.map(
                  ({
                    key,
                    label,
                    required,
                  }) => {
                    const document =
                      docState[
                        key
                      ];

                    const hasFile =
                      Boolean(
                        document.file_url
                      );

                    const isDownloading =
                      downloadingKey ===
                      key;

                    const isIncomplete =
                      required &&
                      (
                        !document.expiry_date ||
                        !document.file_url
                      );

                    return (
                      <div
                        key={key}
                        className={`vdl-form-doc-card ${
                          hasFile
                            ? "vdl-form-doc-card--filled"
                            : ""
                        } ${
                          isIncomplete
                            ? "vdl-form-doc-card--required-missing"
                            : ""
                        }`}
                      >

                        <div className="vdl-form-doc-card-top">

                          <span className="vdl-form-doc-label">
                            {label}

                            {required && (
                              <span className="vdl-form-doc-required-mark">
                                {" "}*
                              </span>
                            )}
                          </span>

                          {hasFile && (
                            <span className="vdl-form-doc-badge">
                              <FiCheck
                                size={11}
                              />
                              Uploaded
                            </span>
                          )}

                        </div>


                        <div className="vdl-form-doc-row">

                          <div className="vdl-form-doc-date">

                            <span>
                              Expiry Date
                              {required
                                ? " *"
                                : ""}
                            </span>

                            <input
                              type="date"
                              value={
                                document.expiry_date ||
                                ""
                              }
                              onChange={(
                                event
                              ) =>
                                handleDateChange(
                                  key,
                                  event
                                    .target
                                    .value
                                )
                              }
                              disabled={
                                loadingVehicleData ||
                                !autofill
                              }
                            />

                          </div>


                          <div className="vdl-form-doc-upload-wrap">

                            <span>
                              &nbsp;
                            </span>

                            <label className="vdl-form-doc-upload">

                              <input
                                type="file"
                                disabled={
                                  loadingVehicleData ||
                                  document.uploading ||
                                  !autofill
                                }
                                onChange={(
                                  event
                                ) =>
                                  handleFileChange(
                                    key,
                                    event
                                      .target
                                      .files?.[0]
                                  )
                                }
                              />

                              <span className="vdl-form-upload-btn">
                                <FiUpload
                                  size={12}
                                />

                                {document.uploading
                                  ? "Uploading…"
                                  : hasFile
                                  ? "Replace"
                                  : "Upload"}
                              </span>

                            </label>

                          </div>

                        </div>


                        <div className="vdl-form-doc-row">

                          <button
                            type="button"
                            className="vdl-form-preview-btn"
                            onClick={() =>
                              openPreview(
                                document.file_url,
                                label,
                                document.original_filename
                              )
                            }
                            disabled={
                              !hasFile ||
                              previewLoading
                            }
                          >
                            <FiEye
                              size={12}
                            />
                            {previewLoading
                              ? "Loading…"
                              : "Preview"}
                          </button>


                          <button
                            type="button"
                            className="vdl-form-download-btn"
                            onClick={() =>
                              handleDownload(
                                document.file_url,
                                document.original_filename ||
                                  `${label}`,
                                key
                              )
                            }
                            disabled={
                              !hasFile ||
                              isDownloading
                            }
                          >
                            <FiDownload
                              size={12}
                            />

                            {isDownloading
                              ? "Downloading…"
                              : "Download"}
                          </button>

                        </div>

                      </div>
                    );
                  }
                )}

              </div>


              {documentsRowId && (
                <div className="vdl-form-doc-meta">

                  <span>
                    Created:{" "}
                    {formatUTCTimestamp(
                      documentsMeta.createdAt
                    )}

                    {documentsMeta.createdBy
                      ? ` by ${documentsMeta.createdBy}`
                      : ""}
                  </span>

                  <span>
                    Last updated:{" "}
                    {formatUTCTimestamp(
                      documentsMeta.updatedAt
                    )}

                    {documentsMeta.updatedBy
                      ? ` by ${documentsMeta.updatedBy}`
                      : ""}
                  </span>

                </div>
              )}

            </div>


            {status && (
              <div
                className={`vdl-form-status vdl-form-status--${status.type}`}
              >
                {status.message}
              </div>
            )}


            <div className="vdl-form-footer">

              <button
                className="vdl-form-cancel-btn"
                onClick={
                  handleBack
                }
                type="button"
                disabled={
                  saving
                }
              >
                Cancel
              </button>


              <button
                className="vdl-form-save-btn"
                onClick={
                  handleSave
                }
                type="button"
                disabled={
                  !vehicleId ||
                  saving ||
                  loadingVehicleData
                }
              >
                {saving
                  ? "Saving…"
                  : "Save Documents"}
              </button>

            </div>

          </div>
        </div>


        {previewDoc && (
          <div
            className="vdl-form-lightbox-overlay"
            onClick={
              closePreview
            }
          >

            <div
              className="vdl-form-lightbox"
              onClick={(event) =>
                event.stopPropagation()
              }
              onContextMenu={(event) =>
                event.preventDefault()
              }
            >

              <div className="vdl-form-lightbox-header">

                <span>
                  {
                    previewDoc.label
                  }
                </span>

                <button
                  type="button"
                  onClick={
                    closePreview
                  }
                  aria-label="Close preview"
                >
                  <FiX
                    size={18}
                  />
                </button>

              </div>


              <div className="vdl-form-lightbox-body">

                {previewDoc.kind ===
                  "image" && (
                  <img
                    src={
                      previewDoc.url
                    }
                    alt={
                      previewDoc.label
                    }
                    draggable={
                      false
                    }
                    onContextMenu={(
                      event
                    ) =>
                      event.preventDefault()
                    }
                  />
                )}


                {previewDoc.kind ===
                  "pdf" && (
                  <iframe
                    src={`${previewDoc.url}#toolbar=0`}
                    title={
                      previewDoc.label
                    }
                    style={{
                      width:
                        "100%",
                      height:
                        "70vh",
                      border:
                        "none",
                    }}
                  />
                )}


                {previewDoc.kind ===
                  "other" && (
                  <div
                    style={{
                      padding:
                        "2rem",
                      textAlign:
                        "center",
                    }}
                  >
                    <FiFileText
                      size={32}
                    />

                    <p>
                      Preview isn't
                      available
                      for this file
                      type.
                    </p>
                  </div>
                )}

              </div>

            </div>

          </div>
        )}

      </div>
    </div>
  );
}